import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import type { InviteMemberFormData } from '@/components/org-settings/members/members.interface'

import type { ClerkInvitationsResource, ClerkOrganizationResource } from './members-clerk.types'

interface UseInvitationOperationsProps {
  organization: ClerkOrganizationResource | null
  invitations: ClerkInvitationsResource | null
  isAtMemberLimit: boolean
}

interface UseInvitationOperationsReturn {
  isInviting: boolean
  handleInvite: (data: InviteMemberFormData) => Promise<void>
  handleRevokeInvitation: (invitationId: string) => Promise<void>
}

export const useInvitationOperations = ({
  organization,
  invitations,
  isAtMemberLimit,
}: UseInvitationOperationsProps): UseInvitationOperationsReturn => {
  const [isInviting, setIsInviting] = useState(false)

  const handleInvite = useCallback(
    async (data: InviteMemberFormData): Promise<void> => {
      if (!organization) return

      if (isAtMemberLimit) {
        toast.error('Member limit reached. Upgrade your plan to add more members.')
        return
      }

      setIsInviting(true)
      try {
        await organization.inviteMember({ emailAddress: data.email, role: data.memberRole })
        await invitations?.revalidate?.()
        trackEvent('member_invited', { role: data.memberRole })
        toast.success(`Invitation sent to ${data.email}`)
      } catch (error) {
        logger.error('Failed to invite member:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation'
        toast.error(errorMessage)
      } finally {
        setIsInviting(false)
      }
    },
    [organization, invitations, isAtMemberLimit]
  )

  const handleRevokeInvitation = useCallback(
    async (invitationId: string): Promise<void> => {
      const invitation = invitations?.data?.find((i) => i.id === invitationId)
      if (!invitation) return

      try {
        await invitation.revoke()
        await invitations?.revalidate?.()
        trackEvent('invitation_revoked', {})
        toast.success('Invitation revoked')
      } catch (error) {
        logger.error('Failed to revoke invitation:', error)
        toast.error('Failed to revoke invitation')
      }
    },
    [invitations]
  )

  return { isInviting, handleInvite, handleRevokeInvitation }
}
